import ExportButton from '../../../components/ExportButton';
import { useEffect, useState } from 'react';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../../components/Breadcrumbs';
import Button from '../../../components/button';
import CustomModal from '../../../components/CustomModal';
import Filters from '../../../components/Filters';
import NewTableComponent from '../../../components/NewTableComponent';
import Stats from '../../../components/Stats';
import { fetchPartCategoriesDropdown } from '../../../store/Slices/dropdownSlice';
import { deleteParts, fetchParts, fetchPartsOverview } from '../../../store/Slices/PartsSlice';
import '../../../styles/Services.css';
import { lowStockIcon, partUtilizedIcon, partValueIcon, plusIcon, typeStock } from '../../../utils/svgs';
import { errorNotify, successNotify } from '../../../utils/toast';
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, PartsPermission } from '../../../utils/app-constant';
import { usePartsListingExport } from './usePartsListingExport';

const Parts = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getPartsPaginated, getPartsLoading, deletePartsLoading, fetchPartsOverviewData } = useSelector((state) => state.parts)
    const { partCategoriesDropdownData } = useSelector((state) => state.dropdowns);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [deleteUserPopup, setDeleteUserPopup] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [searchFilters, setSearchFilters] = useState(null);
    const [openFilterName, setOpenFilterName] = useState(null);

    const {
        hasActiveFilters,
        exportLoading,
        pdfExportLoading,
        excelExportLoading,
        handleExportPdf,
        handleExportExcel,
    } = usePartsListingExport(searchText, searchFilters);

    useEffect(() => {
        dispatch(fetchPartCategoriesDropdown());
        dispatch(fetchPartsOverview());
    }, [dispatch]);

    useEffect(() => {
        if (searchText.length > 0 || searchFilters) {
            dispatch(fetchParts({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
        }
        else {
            dispatch(fetchParts({
                page: currentPage,
                limit: limit,
            }));
        }
    }, [searchText, currentPage, limit, searchFilters]);

    const commonColumns = {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
    }

    const renderCompactCell = (value) => {
        const text = value || "-";
        return (
            <span
                title={text}
                style={{
                    display: "inline-block",
                    width: "100%",
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    verticalAlign: "middle",
                }}
            >
                {text}
            </span>
        );
    };

    const columns = [
        {
            ...commonColumns,
            name: "Part ID",
            key: "part_id",
            selector: (row) => (
                <span
                    className="hyperlink"
                    onClick={() => navigate(`/parts/details/${row?.id}`)}
                    style={{ cursor: 'pointer' }}
                >
                    {`${row?.part_id}`}
                </span>
            ),
            tableColumn: false,
        },
        {
            ...commonColumns,
            name: "Part Name",
            key: "part_name",
            selector: (row) => row?.name || "-",
            cell: (row) => renderCompactCell(row?.name),
            tableColumn: true,
            filterName: "nameFilter",
            type: "text",
            width: "260px",
            wrap: true,
        },
        {
            ...commonColumns,
            name: "SKU",
            key: "sku",
            selector: (row) => row?.sku || "-",
            cell: (row) => renderCompactCell(row?.sku),
            tableColumn: true,
            filterName: "skuFilter",
            type: "text",
            width: "230px",
            wrap: true,
        },
        {
            ...commonColumns,
            name: "Part Number",
            key: "part_number",
            selector: (row) => row?.part_number || "-",
            cell: (row) => renderCompactCell(row?.part_number),
            tableColumn: true,
            filterName: "partNumberFilter",
            type: "text",
            width: "260px",
            wrap: true,
        },
        {
            ...commonColumns,
            name: "Category",
            key: "category",
            selector: (row) => row?.category ? row?.category?.title : "-",
            cell: (row) => renderCompactCell(row?.category ? row?.category?.title : "-"),
            tableColumn: true,
            filterName: "categoryFilter",
            type: "select",
            options: partCategoriesDropdownData,
            width: "220px",
            wrap: true,
        },
        {
            ...commonColumns,
            name: "Available Stock Count",
            key: "available_stock_count",
            selector: (row) => row?.stock_count,
            tableColumn: true,
            filterName: "stockCountFilter",
            type: "number",
            minWidth: "230px",
            width: "230px",
        },
        {
            name: "Actions",
            key: "actions",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: false,
            selector: (row) => (
                <div className="table_action_btn">
                    <PermissionWrapper
                        permission={PartsPermission.Delete}
                        module={InventorySubModules.Parts}
                        mainModule={AppMainModules.inventoryManagement}
                    >
                        <button onClick={() => {
                            setDeleteUserPopup(true);
                            setDeleteUserId(row?.id);
                        }} className="delete_btn">
                            <RiDeleteBinLine />
                        </button>
                    </PermissionWrapper>
                </div>
            ),
            sortable: false,
            tableColumn: false
        }
    ].filter(Boolean);

    const handleDeleteParts = async () => {
        try {
            await dispatch(deleteParts(deleteUserId)).unwrap();
            successNotify('Part deleted successfully');
            setDeleteUserPopup(false);
            setDeleteUserId(null);

            dispatch(fetchPartsOverview());
            dispatch(fetchParts({
                page: currentPage,
                limit: limit,
                searchFilters: searchFilters
            }));
        } catch (error) {
            errorNotify(error);
        }
    };

    const handleLimitChange = (newLimit) => {
        setCurrentPage(1);
        setLimit(newLimit);
    };

    const handleSearch = (text) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    const processedResults = (getPartsPaginated?.results || []).map((item, idx, arr) => {
        const isLastFour = idx >= arr.length - 4;
        return { ...item, isLastFourServiceRow: isLastFour };
    });

    const parseStatValue = (value, fallback = 0) => {
        if (value === undefined || value === null || value === "") return fallback;
        const rawValue = typeof value === "string" ? value.replace(/,/g, "").trim() : value;
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const getOverviewMetric = (keys, fallback = 0) => {
        const source = fetchPartsOverviewData || {};
        for (const key of keys) {
            const directValue = source?.[key];
            if (directValue !== undefined && directValue !== null && directValue !== "") {
                return parseStatValue(directValue, fallback);
            }

            const nestedValue = source?.[key]?.value ?? source?.[key]?.total ?? source?.[key]?.count;
            if (nestedValue !== undefined && nestedValue !== null && nestedValue !== "") {
                return parseStatValue(nestedValue, fallback);
            }
        }
        return fallback;
    };

    const formatCompact = (value) => {
        const numericValue = parseStatValue(value, 0);
        return new Intl.NumberFormat("en-US", {
            notation: "compact",
            maximumFractionDigits: 1,
        }).format(numericValue);
    };

    const formatNumber = (value) => parseStatValue(value, 0).toLocaleString();

    const totalPartsValue = getOverviewMetric([
        "total_parts_value",
        "totalPartsValue",
        "parts_value",
        "inventory_valuation",
        "inventoryValuation",
    ]);
    const lowStockAlerts = getOverviewMetric([
        "low_stock_alerts",
        "lowStockAlerts",
        "low_stock_count",
        "lowStockCount",
    ]);
    const totalPartsUtilized = getOverviewMetric([
        "total_parts_utilized",
        "totalPartsUtilized",
        "parts_utilized",
        "partsUtilized",
    ]);
    const totalTyreStock = getOverviewMetric([
        "total_tyre_stock",
        "totalTyreStock",
        "total_tire_stock",
        "totalTireStock",
        "tyre_stock",
        "tire_stock",
    ]);

    const partStatsData = [
        {
            title: "Total Parts Value (PKR)",
            value: formatCompact(totalPartsValue),
            text: (
                <>
                    <span>Current inventory valuation </span>
                </>
            ),
            icon: partValueIcon,
        },
        {
            title: "Low Stock Alerts",
            value: formatNumber(lowStockAlerts),
            text: (
                <>
                    <span>Below minimum stock level</span>
                </>
            ),
            icon: lowStockIcon,
        },
        {
            title: "Total Parts Utilized",
            value: formatNumber(totalPartsUtilized),
            text: (
                <>
                    <span>Issued from inventory</span>
                </>
            ),
            icon: partUtilizedIcon,
        },
        {
            title: "Total Tyre Stock",
            value: formatNumber(totalTyreStock),
            text: (
                <>
                    <span>Available in stores</span>
                </>
            ),
            icon: typeStock,
        },
    ];

    return (
        <div>
            <Breadcrumbs values={['Inventory', 'Parts']} />

            <CustomModal show={deleteUserPopup} handleClose={() => setDeleteUserPopup(false)} size="md" heading="Delete Part" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this Part?</p>

                    <div className='d-flex align-items-center gap-2'>
                        <Button
                            cancelBtn={() => setDeleteUserPopup(false)}
                            submitBtn={handleDeleteParts}
                            loading={deletePartsLoading}
                            disabled={deletePartsLoading}
                            buttonExtraCss="delete_user_modal_btn"
                            submitText="Delete"
                            type="submit"
                            shouldRow={false}
                        />
                    </div>
                </div>
            </CustomModal>

            <div className='access_view'>
                <h2>Parts Inventory Dashboard</h2>

                <div className='services_buttons'>
                    <ExportButton
                        type="pdf"
                        onClick={handleExportPdf}
                        disabled={!hasActiveFilters || exportLoading}
                        loading={pdfExportLoading}
                        hasActiveFilters={hasActiveFilters}
                    />

                    <ExportButton
                        type="excel"
                        onClick={handleExportExcel}
                        disabled={!hasActiveFilters || exportLoading}
                        loading={excelExportLoading}
                        hasActiveFilters={hasActiveFilters}
                    />

                    <PermissionWrapper
                        permission={PartsPermission.Create}
                        module={InventorySubModules.Parts}
                        mainModule={AppMainModules.inventoryManagement}
                    >
                        <button onClick={() => navigate('/parts/create')}>{plusIcon} Add New Part</button>
                    </PermissionWrapper>
                </div>
            </div>

            <Stats statsData={partStatsData} />

            {openFilterName && (
                <Filters
                    closeFilter={() => setOpenFilterName(null)}
                    searchFilters={searchFilters}
                    setSearchFilters={setSearchFilters}
                    filterName={openFilterName}
                    type={columns.find(col => col.filterName === openFilterName)?.type || "text"}
                />
            )}

            <NewTableComponent
                tableClassName="parts_table_fix"
                showAddBtn={false}
                columns={columns}
                data={processedResults}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={handleLimitChange}
                totalItems={getPartsPaginated?.meta?.totalItems}
                totalPages={getPartsPaginated?.meta?.totalPages}
                onSearch={handleSearch}
                loading={getPartsLoading}
                paginationLoading={getPartsLoading}
                setSearchFilters={setSearchFilters}
                searchFilters={searchFilters}
            />
        </div>
    )
}

export default Parts
